import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getFirebaseDB, getFirebaseStorage } from "@/lib/firebase";
import { MaintenanceRequest } from "@/types/types";
import moment from "moment";
import {
  notifyMaintenanceRequest,
  notifyMaintenanceUpdate,
} from "@/lib/notification-service";

// Types for service functions
export interface CreateMaintenanceRequestData {
  propertyId: string;
  propertyTitle: string;
  propertyAddress: string;
  unitId: string;
  unitName: string;
  tenantId: string;
  tenantName: string;
  tenantEmail: string;
  tenantPhone: string;

  category: MaintenanceRequest["category"];
  description: string;
  priority: MaintenanceRequest["priority"];
  status?: MaintenanceRequest["status"];

  title: string;
  submittedBy: string;
}

export interface UpdateMaintenanceRequestData {
  category?: MaintenanceRequest["category"];
  description?: string;
  priority?: MaintenanceRequest["priority"];
  status?: MaintenanceRequest["status"];
  title?: string;
  assignedTo?: string;
  assignedPhone?: string;
  estimatedCost?: number;
  actualCost?: number;
  scheduledDate?: string;
  completedDate?: string;
  images?: string[];
  notes?: string;
  feedback?: string;
  rating?: number;
}

// Form schemas for validation (similar patterns as in component)
export const maintenanceFormSchema = {
  property: { required_error: "Please select a property" },
  unit: { required_error: "Please select a unit" },
  tenant: { required_error: "Please select a tenant" },
  category: { required_error: "Please select a category" },
  title: {
    min: { message: "Title must be at least 5 characters" },
    max: { message: "Title must be less than 100 characters" },
  },
  description: {
    min: { message: "Description must be at least 10 characters" },
    max: { message: "Description must be less than 500 characters" },
  },
  priority: { required_error: "Please select a priority level" },
  status: { required_error: "Please select a status" },
};

export const statusUpdateFormSchema = {
  status: { required_error: "Please select a status" },
  scheduledDate: { type: "date" },
  assignedTo: { max: { message: "Name too long" } },
  assignedPhone: {
    pattern: { value: /^[\d\s\-\+\(\)]+$/, message: "Invalid phone format" },
  },
  actualCost: { min: { value: 0, message: "Cost cannot be negative" } },
  completedDate: { type: "date" },
  notes: { max: { message: "Notes too long" } },
};

/**
 * Add a new maintenance request to Firestore
 */
export async function addMaintenanceRequest(
  requestData: CreateMaintenanceRequestData
): Promise<MaintenanceRequest> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    const now = moment().toISOString();

    const firestoreData = {
      ...requestData,
      status: requestData.status || "pending",
      dateSubmitted: now,
      dateUpdated: now,
      createdAt: now,
      updatedAt: now,
      // Future fields
      estimatedCost: 0,
      actualCost: 0,
    };

    const docRef = await addDoc(
      collection(db, "maintenanceRequests"),
      firestoreData
    );

    // Send notification to landlord
    try {
      // Get landlordId from property
      const propertyRef = doc(db, "properties", requestData.propertyId);
      const propertySnap = await getDoc(propertyRef);
      if (propertySnap.exists()) {
        const landlordId = propertySnap.data().ownerId;
        if (landlordId) {
          await notifyMaintenanceRequest(
            landlordId,
            docRef.id,
            requestData.propertyTitle,
            requestData.category
          );
        }
      }
    } catch (notifError) {
      console.error("Failed to send notification:", notifError);
      // Don't fail the request creation if notification fails
    }

    return {
      id: docRef.id,
      ...firestoreData,
    } as MaintenanceRequest;
  } catch (error) {
    console.error("Error adding maintenance request:", error);
    throw new Error(
      `Failed to add maintenance request: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Get maintenance requests for a specific user (landlord or tenant)
 */
export async function getMaintenanceRequests(options: {
  userId?: string;
  userRole?: "landlord" | "tenant";
  propertyId?: string;
  status?: MaintenanceRequest["status"];
  limit?: number;
}): Promise<MaintenanceRequest[]> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    let baseQuery = collection(db, "maintenanceRequests");

    // Build query dynamically
    const constraints: any[] = [];

    if (options.userId && options.userRole) {
      if (options.userRole === "landlord") {
        // For landlords, get requests from their properties
        const propertiesRef = collection(db, "properties");
        const propertiesQuery = query(
          propertiesRef,
          where("ownerId", "==", options.userId)
        );

        // Get property IDs first
        const propertiesSnap = await getDocs(propertiesQuery);
        const propertyIds = propertiesSnap.docs.map((doc) => doc.id);

        if (propertyIds.length > 0) {
          constraints.push(where("propertyId", "in", propertyIds.slice(0, 10))); // Firestore limit
        } else {
          // No properties, return empty array
          return [];
        }
      } else if (options.userRole === "tenant") {
        constraints.push(where("tenantId", "==", options.userId));
      }
    }

    if (options.propertyId) {
      constraints.push(where("propertyId", "==", options.propertyId));
    }

    if (options.status) {
      constraints.push(where("status", "==", options.status));
    }

    constraints.push(orderBy("dateSubmitted", "desc"));

    const q = query(baseQuery, ...constraints);

    const querySnapshot = await getDocs(q);
    let requests: MaintenanceRequest[] = [];

    querySnapshot.forEach((doc) => {
      requests.push({
        id: doc.id,
        ...doc.data(),
      } as MaintenanceRequest);
    });

    // Apply client-side limit if specified
    if (options.limit && requests.length > options.limit) {
      requests = requests.slice(0, options.limit);
    }

    return requests;
  } catch (error) {
    console.error("Error getting maintenance requests:", error);
    throw new Error(
      `Failed to get maintenance requests: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Get a maintenance request by ID
 */
export async function getMaintenanceRequestById(
  requestId: string
): Promise<MaintenanceRequest | null> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    const docRef = doc(db, "maintenanceRequests", requestId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as MaintenanceRequest;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting maintenance request:", error);
    throw new Error(
      `Failed to get maintenance request: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Update a maintenance request
 */
export async function updateMaintenanceRequest(
  requestId: string,
  updateData: UpdateMaintenanceRequestData
): Promise<MaintenanceRequest | null> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    const docRef = doc(db, "maintenanceRequests", requestId);

    // Get current request to merge data
    const currentRequest = await getMaintenanceRequestById(requestId);
    if (!currentRequest) {
      throw new Error("Maintenance request not found");
    }

    const updatedData = {
      ...updateData,
      dateUpdated: moment().toISOString(),
      updatedAt: moment().toISOString(),
    };

    await updateDoc(docRef, updatedData);

    // Send notification to tenant if status changed
    if (updateData.status && updateData.status !== currentRequest.status) {
      try {
        await notifyMaintenanceUpdate(
          currentRequest.tenantId,
          requestId,
          updateData.status,
          currentRequest.category
        );
      } catch (notifError) {
        console.error("Failed to send status update notification:", notifError);
        // Don't fail the update if notification fails
      }
    }

    // Return updated request
    return {
      ...currentRequest,
      ...updatedData,
    };
  } catch (error) {
    console.error("Error updating maintenance request:", error);
    throw new Error(
      `Failed to update maintenance request: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Delete a maintenance request
 */
export async function deleteMaintenanceRequest(
  requestId: string
): Promise<void> {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    const docRef = doc(db, "maintenanceRequests", requestId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting maintenance request:", error);
    throw new Error(
      `Failed to delete maintenance request: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Update maintenance request status
 */
export async function updateMaintenanceStatus(
  requestId: string,
  status: MaintenanceRequest["status"],
  additionalData?: Partial<UpdateMaintenanceRequestData>
): Promise<MaintenanceRequest | null> {
  return updateMaintenanceRequest(requestId, {
    status,
    ...additionalData,
  });
}

/**
 * Get statistics for maintenance requests
 */
export async function getMaintenanceStats(options: {
  userId?: string;
  userRole?: "landlord" | "tenant";
  propertyId?: string;
}) {
  try {
    const requests = await getMaintenanceRequests(options);

    const stats = {
      total: requests.length,
      pending: requests.filter((r) => r.status === "pending").length,
      scheduled: requests.filter((r) => r.status === "scheduled").length,
      inProgress: requests.filter((r) => r.status === "in-progress").length,
      completed: requests.filter((r) => r.status === "completed").length,
      cancelled: requests.filter((r) => r.status === "cancelled").length,
      highPriority: requests.filter(
        (r) =>
          r.priority === "high" &&
          r.status !== "completed" &&
          r.status !== "cancelled"
      ).length,
      totalCost: requests
        .filter((r) => r.actualCost !== undefined && r.actualCost !== null)
        .reduce((sum, r) => sum + (r.actualCost || 0), 0),
      averageResolutionTime: calculateAverageResolutionTime(requests),
      requestsByCategory: calculateRequestsByCategory(requests),
      requestsByProperty: calculateRequestsByProperty(requests),
    };

    return stats;
  } catch (error) {
    console.error("Error getting maintenance stats:", error);
    throw new Error(
      `Failed to get maintenance stats: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

/**
 * Listen to real-time updates for maintenance requests
 */
export function subscribeToMaintenanceRequests(
  onUpdate: (requests: MaintenanceRequest[]) => void,
  onError?: (error: Error) => void,
  options?: {
    userId?: string;
    userRole?: "landlord" | "tenant";
    propertyId?: string;
  }
) {
  try {
    const db = getFirebaseDB();
    if (!db) throw new Error("Firebase DB not available");

    let baseQuery = collection(db, "maintenanceRequests");
    const constraints: any[] = [];

    if (options?.userId && options?.userRole) {
      if (options.userRole === "landlord") {
        // For landlords, we need to get their property IDs first
        console.warn(
          "Real-time subscription for landlords requires property IDs"
        );
        // This would need additional logic or cached property IDs
      } else if (options.userRole === "tenant") {
        constraints.push(where("tenantId", "==", options.userId));
      }
    }

    constraints.push(orderBy("dateUpdated", "desc"));

    const q = query(baseQuery, ...constraints);

    return onSnapshot(
      q,
      (querySnapshot) => {
        const requests: MaintenanceRequest[] = [];
        querySnapshot.forEach((doc) => {
          requests.push({
            id: doc.id,
            ...doc.data(),
          } as MaintenanceRequest);
        });
        onUpdate(requests);
      },
      (error) => {
        console.error("Error listening to maintenance requests:", error);
        if (onError) onError(error);
      }
    );
  } catch (error) {
    console.error("Error setting up maintenance listener:", error);
    if (onError) onError(error as Error);
  }
}

/**
 * Upload maintenance images
 */
export async function uploadMaintenanceImages(
  files: File[],
  requestId: string = "new-request"
): Promise<string[]> {
  if (!files || files.length === 0) {
    return [];
  }

  try {
    const storage = getFirebaseStorage();
    if (!storage) throw new Error("Firebase Storage not available");

    const uploadedUrls: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file
      if (!file.type.startsWith("image/")) {
        throw new Error(
          `File ${i + 1}: Invalid file type. Only images are allowed.`
        );
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error(`File ${i + 1}: File size exceeds 5MB limit.`);
      }

      // Create a unique filename
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 9);
      const filename = `${timestamp}-${randomId}-${file.name}`;

      // Create storage reference
      const storageRef = ref(
        storage,
        `maintenance/${requestId}/images/${filename}`
      );

      // Upload file
      const snapshot = await uploadBytes(storageRef, file);

      // Get download URL
      const downloadUrl = await getDownloadURL(snapshot.ref);
      uploadedUrls.push(downloadUrl);
    }

    return uploadedUrls;
  } catch (error) {
    console.error("Error uploading maintenance images:", error);
    throw new Error(
      `Failed to upload images: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
}

// Helper functions
function calculateAverageResolutionTime(
  requests: MaintenanceRequest[]
): number {
  const completedRequests = requests.filter(
    (r) => r.status === "completed" && r.completedDate && r.dateSubmitted
  );

  if (completedRequests.length === 0) return 0;

  const totalDays = completedRequests.reduce((sum, request) => {
    const submitted = new Date(request.dateSubmitted);
    const completed = new Date(request.completedDate!);
    const diffDays =
      (completed.getTime() - submitted.getTime()) / (1000 * 60 * 60 * 24);
    return sum + diffDays;
  }, 0);

  return totalDays / completedRequests.length;
}

function calculateRequestsByCategory(
  requests: MaintenanceRequest[]
): Record<string, number> {
  const categories: Record<string, number> = {};

  requests.forEach((request) => {
    categories[request.category] = (categories[request.category] || 0) + 1;
  });

  return categories;
}

function calculateRequestsByProperty(requests: MaintenanceRequest[]): Array<{
  propertyId: string;
  propertyName: string;
  count: number;
}> {
  const properties: Record<string, { name: string; count: number }> = {};

  requests.forEach((request) => {
    const key = request.propertyId;
    if (!properties[key]) {
      properties[key] = {
        name: request.propertyTitle,
        count: 0,
      };
    }
    properties[key].count++;
  });

  return Object.entries(properties).map(([propertyId, data]) => ({
    propertyId,
    propertyName: data.name,
    count: data.count,
  }));
}
